/**
 * Pending deep-link target: someone scans a quest/contact/order QR from a
 * poster BEFORE joining. The /q /c /o code is kept across the join + identity
 * reveal (sessionStorage survives the anonymous-session bootstrap and page
 * reloads; the /join?resolve=… search param survives a fresh tab) and is
 * dispatched right after "Let's go".
 */

export type ResolveKind = "q" | "c" | "o";

export interface PendingResolve {
  kind: ResolveKind;
  code: string;
}

const KEY = "ec2.pendingResolve";
/** A poster scan older than this is stale — don't surprise-solve later. */
const MAX_AGE_MS = 30 * 60 * 1000;

const CODE_RE: Record<ResolveKind, RegExp> = {
  q: /^[0-9A-HJKMNP-TV-Z]{6}$/,
  c: /^[0-9A-HJKMNP-TV-Z]{8}$/,
  o: /^[0-9A-HJKMNP-TV-Z]{8}$/,
};

/** `q:X4PMH2` — the /join?resolve=… wire format. */
export function encodePendingResolve(pending: PendingResolve): string {
  return `${pending.kind}:${pending.code}`;
}

/** Strict parse (kind + Crockford alphabet + length); null for garbage. */
export function parsePendingResolve(raw: string | undefined | null): PendingResolve | null {
  if (!raw) return null;
  const match = /^([qco]):([0-9A-HJKMNP-TV-Za-hjkmnp-tv-z]+)$/.exec(raw.trim());
  if (!match) return null;
  const kind = match[1] as ResolveKind;
  const code = match[2]!.toUpperCase();
  return CODE_RE[kind].test(code) ? { kind, code } : null;
}

function storage(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function storePendingResolve(pending: PendingResolve, now: number = Date.now()): void {
  try {
    storage()?.setItem(KEY, JSON.stringify({ ...pending, at: now }));
  } catch {
    /* private mode — the ?resolve search param still carries it */
  }
}

/** Read + validate + CLEAR (single use); expired/garbage entries are dropped. */
export function takePendingResolve(now: number = Date.now()): PendingResolve | null {
  const store = storage();
  if (!store) return null;
  let raw: string | null = null;
  try {
    raw = store.getItem(KEY);
    store.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { kind?: string; code?: string; at?: number };
    if (typeof parsed.at !== "number" || now - parsed.at > MAX_AGE_MS) return null;
    return parsePendingResolve(`${parsed.kind}:${parsed.code}`);
  } catch {
    return null;
  }
}

/** Router target for a pending resolve (routes /q/$code /c/$code /o/$code). */
export function pendingResolvePath(pending: PendingResolve): { to: string; params: { code: string } } {
  return { to: `/${pending.kind}/$code`, params: { code: pending.code } };
}
